import { isDomainName } from "@authory-packages/core";
import { RequestHost } from "../types/headers";

const NEXT_JS_DEFAULT_PORT = 3000;

/**
 * Gets the absolute URL to route to.
 * @param href the path we want to route to
 * @param host the host we want to get the url for. Defaults to the main host.
 * @returns
 */
export function getAbsoluteUrl(href: string = "", host?: string) {
    const protocol: string =
        process.env.NODE_ENV === "development" ? "http" : "https";

    if (!host) {
        host = process.env.NEXT_PUBLIC_MAIN_HOST ?? "localhost";
    }

    return `${protocol}://${host}${href}`;
}

/**
 * Gets the url for the root domain based on a subdomain. Helps with redirection.
 * @param secondaryDomain secondary domain.
 * @param path path we want to navigate to.
 */
export function getPrimaryDomainUrl(
    secondaryDomain: string,
    path: string = ""
) {
    let host = secondaryDomain.substring(secondaryDomain.indexOf(".") + 1);

    // Edge Case: Add port number if we're running the application locally
    if (process.env.NODE_ENV === "development") {
        host = `${host}:${NEXT_JS_DEFAULT_PORT}`;
    }

    return getAbsoluteUrl(path, host);
}

/**
 * Checks whether we are on the main domain or not.
 * @param requestHostHeader
 * @returns
 */
export function isMainDomain(requestHostHeader: RequestHost) {
    return requestHostHeader === RequestHost.MAINDOMAIN;
}

/**
 * Checks whether a request host header points at the main Authory domain.
 * Mirrors the main domain check done in the middleware, for routes the
 * middleware matcher skips (e.g. root files like /sitemap.xml) and therefore
 * never receive the x-authory-request-host header.
 * @param requestHost the raw host header of the request
 * @returns
 */
export function isMainDomainHost(requestHost?: string) {
    if (!requestHost) return false;

    const [hostname] = requestHost.split(":");

    return !isDomainName(hostname) || requestHost === process.env.NEXT_PUBLIC_MAIN_HOST;
}

/**
 * 
 * @param host Formats an url with protocol
 * @returns 
 */

export function getCustomDomainURL(host: string) {
    const protocol: string =
        process.env.NODE_ENV === "development" ? "http" : "https";

    if (!host) {
        host = process.env.NEXT_PUBLIC_MAIN_HOST ?? "localhost";
    }

    return `${protocol}://${host}`;
}

/**
 * Checks whether a request host header points at an Authory subdomain
 * (e.g. jane.authory.com) rather than the main domain or a custom domain.
 * Mirrors the subdomain check done in the proxy, for routes the proxy matcher
 * skips (e.g. root files like /robots.txt).
 * @param requestHost the raw host header of the request
 * @returns
 */
export function isAuthorySubdomainHost(requestHost?: string) {
    if (!requestHost || isMainDomainHost(requestHost)) return false;

    const [hostname] = requestHost.split(":");
    const mainDomain = process.env.NEXT_PUBLIC_MAIN_DOMAIN ?? "authory.com";

    return hostname.endsWith(`.${mainDomain}`);
}
